import { RESUME_PROMPT_STALE_AFTER_DAYS } from '@poseidon/types/types/cadenceState.type';

import { Button } from '../Input2';
import { Alerts } from '../StrokeIcons';
import Popup from './Popup';

export type PriorSentStepsSummary = {
  /** Outreach steps of the target template already sent to this contact by this rep. */
  alreadySentCount: number;
  /** Total outreach steps in the target template. */
  outreachStepCount: number;
  /** When the most recent of those sends happened (null if unknown). */
  lastSentAt: Date | string | null;
  /** Name of the campaign that send belonged to. */
  priorCampaignName: string | null;
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** Days since `lastSentAt`, or null when the timestamp is missing. */
export function daysSincePriorSend(summary: PriorSentStepsSummary): number | null {
  if (summary.lastSentAt == null) return null;
  const t = new Date(summary.lastSentAt).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / DAY_MS);
}

/**
 * POS-2455: whether enrolling should ASK before resuming.
 *
 * - Fully sent (every outreach step already went out): ALWAYS ask, regardless
 *   of age — a plain enrol would be refused server-side, and the popup is the
 *   only place this surface offers "Start fresh", so skipping it would
 *   dead-end the rep on a refusal toast.
 * - Partially sent: ask only when the last send is older than
 *   {@link RESUME_PROMPT_STALE_AFTER_DAYS}; recent history resumes silently.
 *
 * Only the prompt is gated on age; if the rep chooses Resume, already-sent
 * matching covers all history regardless.
 */
export function shouldPromptResumeOrStartFresh(summary: PriorSentStepsSummary): boolean {
  if (summary.alreadySentCount === 0) return false;
  if (summary.outreachStepCount > 0 && summary.alreadySentCount >= summary.outreachStepCount) return true;
  const days = daysSincePriorSend(summary);
  return days == null || days > RESUME_PROMPT_STALE_AFTER_DAYS;
}

/**
 * "Resume or start fresh?" interstitial, shown when enrolling a contact who
 * already received part of this campaign a long time ago. ESC (via Popup's esc
 * stack) or the backdrop cancels — nothing is enrolled.
 */
export default function ResumeOrStartFreshPopup({
  campaignName,
  summary,
  onResume,
  onStartFresh,
  onClose,
}: {
  campaignName: string;
  summary: PriorSentStepsSummary;
  /** Enrol skipping already-sent steps (the default behaviour). */
  onResume: () => void;
  /** Enrol from the top, re-sending everything. */
  onStartFresh: () => void;
  onClose: () => void;
}) {
  const days = daysSincePriorSend(summary);
  const fullySent = summary.outreachStepCount > 0 && summary.alreadySentCount >= summary.outreachStepCount;
  const when =
    days == null ? 'a while ago' : days >= 365 ? `over ${Math.floor(days / 365)} year(s) ago` : `${days} days ago`;

  return (
    <Popup onClose={onClose} className="max-w-md p-0">
      <div className="flex flex-col items-center gap-2 px-5 pt-5 text-center">
        <div className="flex size-9 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900/20">
          <Alerts.AlertTriangle className="size-4 text-yellow-600 dark:text-yellow-400" />
        </div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          This contact already heard from &ldquo;{campaignName}&rdquo;
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {summary.priorCampaignName != null ? (
            <>
              You sent them {summary.alreadySentCount} of {summary.outreachStepCount} steps via{' '}
              <span className="font-medium text-gray-700 dark:text-gray-300">
                &ldquo;{summary.priorCampaignName}&rdquo;
              </span>
              , last {when}.
            </>
          ) : (
            <>
              You already sent them {summary.alreadySentCount} of {summary.outreachStepCount} steps, last {when}.
            </>
          )}{' '}
          {fullySent ? (
            <>
              Every step already went out, so resuming would enrol nothing — start fresh to re-send the campaign from
              the top.
            </>
          ) : (
            <>Resume to send only the steps they haven&apos;t received, or start fresh to re-send everything.</>
          )}
        </p>
      </div>

      <div className="flex justify-end gap-2 px-5 py-4">
        <Button size="sm" tone="outline" onClick={onStartFresh}>
          Start fresh
        </Button>
        <Button size="sm" color="blue" onClick={onResume} disabled={fullySent}>
          Resume where it left off
        </Button>
      </div>
    </Popup>
  );
}
